import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AuthUser, Locale, Resource, Action } from '@farm/shared';
import { can as roleCan } from '@farm/shared';
import { api, setAccessToken, ApiError } from '../api/client';
import type { LoginResponseData } from '@farm/shared';

/**
 * Session state.
 *
 * `restore()` is what makes a page reload survive without persisting a token:
 * the HttpOnly refresh cookie is still in the browser, so a single `/auth/refresh`
 * on boot re-establishes the session. Nothing is read from localStorage.
 */
export const useSessionStore = defineStore('session', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  /** True until the boot-time restore has settled, so the router can wait. */
  const ready = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const locale = computed<Locale>(() => user.value?.locale ?? 'lo');

  /**
   * Mirror of the server's permission matrix, for driving UI affordances only.
   * The server checks independently on every request — a hidden button is a
   * courtesy, never a control.
   */
  function can(resource: Resource, action: Action): boolean {
    return user.value ? roleCan(user.value.role, resource, action) : false;
  }

  function apply(data: LoginResponseData): void {
    setAccessToken(data.token);
    user.value = data.user;
  }

  async function login(
    email: string,
    password: string,
    opts: { staySignedIn?: boolean; turnstileToken?: string } = {},
  ): Promise<void> {
    loading.value = true;
    try {
      const data = await api.post<LoginResponseData>('/auth/login', {
        email,
        password,
        staySignedIn: opts.staySignedIn ?? false,
        ...(opts.turnstileToken ? { turnstileToken: opts.turnstileToken } : {}),
      });
      apply(data);
    } finally {
      loading.value = false;
    }
  }

  /** Re-establish the session on boot from the HttpOnly refresh cookie. */
  async function restore(): Promise<void> {
    try {
      const data = await api.post<LoginResponseData>('/auth/refresh', {});
      apply(data);
    } catch (err) {
      // A 401 here is the normal "not signed in" case, not an error worth
      // surfacing. Anything else is worth a console line for debugging.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.warn('Session restore failed', err);
      }
      user.value = null;
      setAccessToken(null);
    } finally {
      ready.value = true;
    }
  }

  async function logout(allDevices = false): Promise<void> {
    try {
      await api.post('/auth/logout', { allDevices });
    } finally {
      // Clear locally regardless: if the network call failed, the user still
      // expects to be signed out on this device.
      user.value = null;
      setAccessToken(null);
    }
  }

  function clear(): void {
    user.value = null;
    setAccessToken(null);
  }

  return { user, loading, ready, isAuthenticated, locale, can, login, restore, logout, clear };
});
